import {useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {useNavigate,useParams} from 'react-router-dom';
import {useAuth} from '../../../contexts/AuthContext';
import {useInvestigation} from '../../../contexts/InvestigationContext';
import {useData} from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {exportToPDF,shareViaEmail} from '../../../utils/exportUtils';
import InvestigationGuide from './InvestigationGuide';

const {FiArrowLeft,FiArrowRight,FiCheck,FiX,FiUser,FiFileText,FiSave,FiUpload,FiInfo,FiDownload,FiPrinter,FiLock,FiCheckCircle,FiLoader,FiBookOpen,FiTrash2,FiEdit3,FiPlus,FiMail,FiShare2,FiActivity,FiCalendar,FiLink,FiShield,FiUnlock,FiAlertTriangle,FiUsers,FiEye}=FiIcons;

const InvestigationForm=()=> {
  const navigate=useNavigate();
  const {reportId}=useParams();
  const {user}=useAuth();
  const {investigations,addInvestigation}=useInvestigation();
  const {reports}=useData();
  const [currentStep,setCurrentStep]=useState(1);
  const [showGuide,setShowGuide]=useState(false);
  const [showReportPreview,setShowReportPreview]=useState(false);
  const [isSaving,setIsSaving]=useState(false);
  
  const [formData,setFormData]=useState({
    status: 'in_progress',
    reportId: reportId || '',
    investigationId: `INV-${Date.now().toString().slice(-6)}`,
    investigationTitle: '',
    investigator: user?.name || '',
    startDate: new Date().toISOString().split('T')[0],
    urgencyLevel: 'medium',
    initialAssessment: {summary: '',severity: 'medium',immediateActions: '',potentialImpact: ''},
    parties: {whistleblower: 'Anonym',reported: '',reportedRole: '',advisor: ''},
    evidence: {timeline: '',documents: [],links: ''},
    meetings: [],
    witnesses: [],
    analysis: {contradictions: '',credibility: '',rootCauseAnalysis: '',systemicIssues: ''},
    conclusion: {substantiated: 'pending',findings: '',reasoning: ''},
    actionPlan: {disciplinary: '',organizational: '',followUpDate: ''},
    finalization: {summary: '',closedDate: new Date().toISOString().split('T')[0],archiveReady: false}
  });

  const sourceReport=reports.find(r=> r.id===reportId);

  useEffect(()=> {
    const existing=investigations?.find(inv=> inv.reportId===reportId);
    if (existing) {
      setFormData(prev=> ({...prev,...existing}));
    } else if (sourceReport) {
      setFormData(prev=> ({
        ...prev,
        investigationTitle: `Utredning: ${sourceReport.title}`,
        parties: {...prev.parties,whistleblower: sourceReport.isAnonymous ? 'Anonym' : sourceReport.reporterName},
        evidence: {...prev.evidence,timeline: sourceReport.description}
      }));
    }
  },[reportId,reports,investigations]);

  const handleInputChange=(step,field,value)=> {
    if (step==='root') {
      setFormData(prev=> ({...prev,[field]: value}));
    } else {
      setFormData(prev=> ({...prev,[step]: {...prev[step],[field]: value}}));
    }
  };

  const addListEntry=(field)=> {
    const newEntry=field==='meetings' ? {id: Date.now(),date: '',participant: '',type: 'Med varsler',summary: ''} : {id: Date.now(),name: '',role: '',observation: ''};
    setFormData(prev=> ({...prev,[field]: [...prev[field],newEntry]}));
  };

  const updateListEntry=(field,id,key,value)=> {
    setFormData(prev=> ({...prev,[field]: prev[field].map(item=> item.id===id ? {...item,[key]: value} : item)}));
  };

  const removeListEntry=(field,id)=> {
    setFormData(prev=> ({...prev,[field]: prev[field].filter(item=> item.id !==id)}));
  };

  const handleSave=async ()=> {
    setIsSaving(true);
    setTimeout(()=> {
      addInvestigation(formData);
      setIsSaving(false);
      navigate('/company/dashboard');
    },800);
  };

  const handleDownloadPDF=()=> {
    exportToPDF(formData,'investigation');
  };

  const inputStyle="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none";
  const labelStyle="block text-xs font-semibold text-gray-500 mb-1 ml-1 uppercase tracking-wide";
  const cardStyle="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-4";
  const headingStyle="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2";

  const PreviewSection=({title,children})=> (
    <div className="mb-8 break-inside-avoid">
      <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest border-b-2 border-indigo-50 pb-2 mb-4">{title}</h4>
      <div className="grid md:grid-cols-2 gap-6">{children}</div>
    </div>
  );

  const PreviewItem=({label,value})=> (
    <div className="space-y-1">
      <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
      <p className="text-sm text-gray-800 whitespace-pre-wrap">{value || '-'}</p>
    </div>
  );

  const renderStepContent=()=> {
    switch (currentStep) {
      case 1: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiFileText} className="text-indigo-600" /> Grunnleggende informasjon</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div><label className={labelStyle}>Utrednings-ID</label><input type="text" value={formData.investigationId} onChange={e=> handleInputChange('root','investigationId',e.target.value)} className={`${inputStyle} font-mono text-indigo-600 font-bold`} /></div>
            <div><label className={labelStyle}>Tittel på utredningen</label><input type="text" value={formData.investigationTitle} onChange={e=> handleInputChange('root','investigationTitle',e.target.value)} className={inputStyle} /></div>
            <div><label className={labelStyle}>Ansvarlig utreder</label><input type="text" value={formData.investigator} onChange={e=> handleInputChange('root','investigator',e.target.value)} className={inputStyle} /></div>
            <div><label className={labelStyle}>Startdato</label><input type="date" value={formData.startDate} onChange={e=> handleInputChange('root','startDate',e.target.value)} className={inputStyle} /></div>
          </div>
        </div>
      );
      case 2: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiActivity} className="text-indigo-600" /> Innledende vurdering</h3>
          <div className="space-y-6">
            <div><label className={labelStyle}>Kort sammendrag av varselet</label><textarea rows={4} value={formData.initialAssessment.summary} onChange={e=> handleInputChange('initialAssessment','summary',e.target.value)} className={inputStyle} /></div>
            <div className="grid md:grid-cols-2 gap-6">
              <div><label className={labelStyle}>Alvorlighetsgrad</label><select value={formData.initialAssessment.severity} onChange={e=> handleInputChange('initialAssessment','severity',e.target.value)} className={inputStyle}><option value="low">Lav</option><option value="medium">Middels</option><option value="high">Høy</option><option value="critical">Kritisk</option></select></div>
              <div><label className={labelStyle}>Potensiell konsekvens</label><input type="text" value={formData.initialAssessment.potentialImpact} onChange={e=> handleInputChange('initialAssessment','potentialImpact',e.target.value)} className={inputStyle} /></div>
            </div>
          </div>
        </div>
      );
      case 3: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiUsers} className="text-indigo-600" /> Involverte parter</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
              <h4 className="font-bold text-blue-900 mb-4 text-sm">Varsler</h4>
              <input type="text" value={formData.parties.whistleblower} onChange={e=> handleInputChange('parties','whistleblower',e.target.value)} className={inputStyle} />
            </div>
            <div className="p-4 bg-orange-50/50 rounded-xl border border-orange-100">
              <h4 className="font-bold text-orange-900 mb-4 text-sm">Omvarslet</h4>
              <div className="space-y-4">
                <input type="text" value={formData.parties.reported} onChange={e=> handleInputChange('parties','reported',e.target.value)} placeholder="Navn" className={inputStyle} />
                <input type="text" value={formData.parties.reportedRole} onChange={e=> handleInputChange('parties','reportedRole',e.target.value)} placeholder="Rolle" className={inputStyle} />
              </div>
            </div>
          </div>
        </div>
      );
      case 4: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiFileText} className="text-indigo-600" /> Bevis og Tidslinje</h3>
          <textarea rows={8} value={formData.evidence.timeline} onChange={e=> handleInputChange('evidence','timeline',e.target.value)} className={inputStyle} />
        </div>
      );
      case 5: return (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className={headingStyle}><SafeIcon icon={FiCalendar} className="text-indigo-600" /> Møteoversikt</h3>
            <button onClick={()=> addListEntry('meetings')} className="text-indigo-600 font-bold flex items-center gap-1 bg-indigo-50 px-3 py-1.5 rounded-lg"><SafeIcon icon={FiPlus} /> Legg til</button>
          </div>
          {formData.meetings.map(meeting=> (
            <div key={meeting.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm relative">
              <button onClick={()=> removeListEntry('meetings',meeting.id)} className="absolute top-4 right-4 text-gray-300 hover:text-red-500"><SafeIcon icon={FiTrash2} /></button>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <select value={meeting.type} onChange={e=> updateListEntry('meetings',meeting.id,'type',e.target.value)} className={inputStyle}><option>Med varsler</option><option>Med omvarslet</option><option>Med vitne</option></select>
                <input type="text" value={meeting.participant} onChange={e=> updateListEntry('meetings',meeting.id,'participant',e.target.value)} placeholder="Deltaker" className={inputStyle} />
              </div>
              <textarea rows={3} value={meeting.summary} onChange={e=> updateListEntry('meetings',meeting.id,'summary',e.target.value)} className={inputStyle} placeholder="Referat..." />
            </div>
          ))}
        </div>
      );
      case 6: return (
        <div className="space-y-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className={headingStyle}><SafeIcon icon={FiEye} className="text-indigo-600" /> Vitner</h3>
            <button onClick={()=> addListEntry('witnesses')} className="text-indigo-600 font-bold flex items-center gap-1 bg-indigo-50 px-3 py-1.5 rounded-lg"><SafeIcon icon={FiPlus} /> Legg til</button>
          </div>
          {formData.witnesses.map(witness=> (
            <div key={witness.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm relative">
              <button onClick={()=> removeListEntry('witnesses',witness.id)} className="absolute top-4 right-4 text-gray-300 hover:text-red-500"><SafeIcon icon={FiTrash2} /></button>
              <input type="text" value={witness.name} onChange={e=> updateListEntry('witnesses',witness.id,'name',e.target.value)} placeholder="Navn" className={inputStyle + " mb-2"} />
              <textarea rows={2} value={witness.observation} onChange={e=> updateListEntry('witnesses',witness.id,'observation',e.target.value)} placeholder="Observasjon..." className={inputStyle} />
            </div>
          ))}
        </div>
      );
      case 7: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiShield} className="text-indigo-600" /> Analyse og Vurdering</h3>
          <div className="space-y-6">
            <textarea rows={4} value={formData.analysis.credibility} onChange={e=> handleInputChange('analysis','credibility',e.target.value)} placeholder="Troverdighetsvurdering..." className={inputStyle} />
            <textarea rows={3} value={formData.analysis.rootCauseAnalysis} onChange={e=> handleInputChange('analysis','rootCauseAnalysis',e.target.value)} placeholder="Rotårsaksanalyse..." className={inputStyle} />
          </div>
        </div>
      );
      case 8: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiCheckCircle} className="text-indigo-600" /> Konklusjon</h3>
          <div className="space-y-6">
            <select value={formData.conclusion.substantiated} onChange={e=> handleInputChange('conclusion','substantiated',e.target.value)} className={`${inputStyle} font-bold`}><option value="pending">Ikke avgjort</option><option value="yes">Ja,kritikkverdige forhold funnet</option><option value="no">Nei</option></select>
            <textarea rows={5} value={formData.conclusion.findings} onChange={e=> handleInputChange('conclusion','findings',e.target.value)} placeholder="Faktiske funn..." className={inputStyle} />
            <textarea rows={5} value={formData.conclusion.reasoning} onChange={e=> handleInputChange('conclusion','reasoning',e.target.value)} placeholder="Begrunnelse..." className={inputStyle} />
          </div>
        </div>
      );
      case 9: return (
        <div className={cardStyle}>
          <h3 className={headingStyle}><SafeIcon icon={FiActivity} className="text-indigo-600" /> Handlingsplan</h3>
          <div className="space-y-6">
            <textarea rows={3} value={formData.actionPlan.disciplinary} onChange={e=> handleInputChange('actionPlan','disciplinary',e.target.value)} placeholder="Disiplinære tiltak..." className={inputStyle} />
            <textarea rows={3} value={formData.actionPlan.organizational} onChange={e=> handleInputChange('actionPlan','organizational',e.target.value)} placeholder="Organisatoriske tiltak..." className={inputStyle} />
          </div>
        </div>
      );
      case 10: return (
        <div className="space-y-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-black text-indigo-900">Fullstendig Utredningsrapport</h2>
            <p className="text-gray-500">Se gjennom hele dokumentet før arkivering.</p>
          </div>
          <div className="bg-white shadow-2xl rounded-sm border border-gray-200 overflow-hidden max-w-5xl mx-auto flex flex-col font-serif min-h-[1000px]">
            <div className="bg-indigo-700 h-3 w-full"></div>
            <div className="p-16 space-y-12">
              <div className="flex justify-between items-start border-b-4 border-gray-900 pb-10">
                <div>
                  <h1 className="text-5xl font-black text-gray-900 tracking-tighter uppercase mb-2">Utredningsrapport</h1>
                  <div className="flex items-center gap-6 text-sm text-gray-500 font-sans font-bold">
                    <span className="flex items-center gap-1 uppercase tracking-widest">ID: {formData.investigationId}</span>
                    <span className="flex items-center gap-1 uppercase tracking-widest">Startdato: {formData.startDate}</span>
                  </div>
                </div>
                <div className="text-right font-sans">
                  <SafeIcon icon={FiShield} size={48} className="text-indigo-600 ml-auto mb-2" />
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Juridisk Protokoll</p>
                </div>
              </div>
              
              <div className="font-sans space-y-10">
                <PreviewSection title="1. Saksinformasjon">
                  <PreviewItem label="Tittel" value={formData.investigationTitle} />
                  <PreviewItem label="Ansvarlig Utreder" value={formData.investigator} />
                  <PreviewItem label="Knyttet til varsel" value={formData.reportId} />
                  <PreviewItem label="Alvorlighetsgrad" value={formData.urgencyLevel.toUpperCase()} />
                </PreviewSection>

                <PreviewSection title="2. Involverte Parter">
                  <PreviewItem label="Varsler (Beskyttet)" value={formData.parties.whistleblower} />
                  <PreviewItem label="Omvarslet person" value={`${formData.parties.reported} (${formData.parties.reportedRole})`} />
                </PreviewSection>

                <PreviewSection title="3. Faktum & Tidslinje">
                  <div className="md:col-span-2 bg-gray-50 p-6 rounded-lg border border-gray-100 italic">
                    {formData.evidence.timeline}
                  </div>
                </PreviewSection>

                <PreviewSection title="4. Gjennomførte Møter">
                  <div className="md:col-span-2 space-y-3">
                    {formData.meetings.map((m,i)=> (
                      <div key={i} className="flex justify-between p-3 bg-gray-50 rounded border-l-4 border-indigo-500">
                        <span className="font-bold text-sm">{m.type} ({m.date})</span>
                        <span className="text-sm text-gray-600">{m.participant}</span>
                      </div>
                    ))}
                  </div>
                </PreviewSection>

                <PreviewSection title="5. Analyse & Konklusjon">
                  <div className="md:col-span-2 space-y-6">
                    <PreviewItem label="Vurdering av bevis" value={formData.analysis.credibility} />
                    <div className="p-6 bg-indigo-50 rounded-xl border border-indigo-100">
                      <PreviewItem label="Endelig Konklusjon" value={formData.conclusion.substantiated==='yes' ? 'KRITIKKVERDIGE FORHOLD BEKREFTET' : 'INGEN KRITIKKVERDIGE FORHOLD FUNNET'} />
                      <div className="mt-4">
                        <PreviewItem label="Begrunnelse" value={formData.conclusion.reasoning} />
                      </div>
                    </div>
                  </div>
                </PreviewSection>

                <PreviewSection title="6. Iverksatte Tiltak">
                  <PreviewItem label="Personelle tiltak" value={formData.actionPlan.disciplinary} />
                  <PreviewItem label="Organisatoriske tiltak" value={formData.actionPlan.organizational} />
                </PreviewSection>
              </div>

              <div className="pt-20 mt-20 border-t border-gray-200 flex justify-between items-center text-[10px] text-gray-400 font-sans tracking-widest uppercase">
                <p>Dokumentet er digitalt signert og låst i HMSVarsling Audit Trail</p>
                <p>Side 1 av 1</p>
              </div>
            </div>
          </div>
          <div className={cardStyle}>
            <h3 className={headingStyle}><SafeIcon icon={FiLock} className="text-indigo-600" /> Ferdigstilling</h3>
            <div className="space-y-4">
              <textarea rows={4} value={formData.finalization.summary} onChange={e=> handleInputChange('finalization','summary',e.target.value)} className={inputStyle} placeholder="Ledelsens oppsummering..." />
              <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg">
                <input type="checkbox" checked={formData.finalization.archiveReady} onChange={e=> handleInputChange('finalization','archiveReady',e.target.checked)} className="h-5 w-5 text-indigo-600 rounded" />
                <label className="text-sm font-medium text-gray-700">Jeg bekrefter at utredningen er korrekt og klar for arkivering.</label>
              </div>
            </div>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans">
      {/* Veileder Modal */}
      <AnimatePresence>
        {showGuide && (
          <motion.div initial={{opacity: 0,y: -20}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,y: -20}} className="fixed inset-0 z-50 bg-white overflow-y-auto">
            <div className="sticky top-0 bg-white border-b z-10 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Veileder</h2>
              <button onClick={()=> setShowGuide(false)} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all" >
                <SafeIcon icon={FiX} size={24} />
              </button>
            </div>
            <InvestigationGuide isEmbedded={true} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* POPUP FOR RAPPORT-VISNING */}
      <AnimatePresence>
        {showReportPreview && (
          <motion.div initial={{opacity: 0,scale: 0.95}} animate={{opacity: 1,scale: 1}} exit={{opacity: 0,scale: 0.95}} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={()=> setShowReportPreview(false)} >
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]" onClick={e=> e.stopPropagation()}>
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-bold text-gray-900">Originalt Varsel</h3>
                <button onClick={()=> setShowReportPreview(false)} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-all" >
                  <SafeIcon icon={FiX} size={24} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                {sourceReport ? (
                  <>
                    <h2 className="text-xl font-bold mb-4">{sourceReport.title}</h2>
                    <div className="space-y-4 text-sm text-gray-700">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="whitespace-pre-wrap">{sourceReport.description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="block font-bold text-gray-500 text-xs uppercase">Dato</span>
                          <span>{new Date(sourceReport.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="block font-bold text-gray-500 text-xs uppercase">Varsler</span>
                          <span>{sourceReport.reporterName || 'Anonym'}</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10">
                    <SafeIcon icon={FiAlertTriangle} className="text-4xl text-yellow-500 mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-gray-900">Fant ikke originalt varsel</h3>
                    <p className="text-gray-500 mt-2 max-w-xs mx-auto">
                      Dette kan skje hvis varselet er slettet, eller hvis dette er en demo-utredning som ikke er koblet til en aktiv rapport.
                    </p>
                    <p className="text-xs text-gray-400 mt-4 font-mono">Referanse-ID: {reportId}</p>
                  </div>
                )}
              </div>
              <div className="p-4 border-t bg-gray-50 text-right">
                <button onClick={()=> setShowReportPreview(false)} className="px-4 py-2 bg-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-300">Lukk</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
          <div className="bg-[#4F46E5] text-white p-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={()=> navigate(-1)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><SafeIcon icon={FiArrowLeft} className="text-xl" /></button>
              <div><h1 className="text-xl font-bold">Utredningsskjema</h1><p className="text-xs text-indigo-200">Steg {currentStep} av 10</p></div>
            </div>
            <div className="flex gap-3">
              <button onClick={()=> setShowReportPreview(true)} className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold border border-white/30 flex items-center gap-2" >
                <SafeIcon icon={FiEye} /> Se Varsel
              </button>
              <button onClick={()=> setShowGuide(true)} className="px-4 py-2 bg-white/20 rounded-lg text-xs font-bold border border-white/30" >
                Åpne Veileder
              </button>
            </div>
          </div>
          <div className="p-8 bg-white min-h-[500px]">{renderStepContent()}</div>
          <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-between items-center">
            <button onClick={()=> setCurrentStep(prev=> Math.max(1,prev - 1))} disabled={currentStep===1} className="px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg font-medium">Forrige</button>
            <div className="flex gap-3">
              {currentStep===10 && (
                <>
                  <button onClick={()=> window.print()} className="px-6 py-2.5 border-2 border-gray-800 rounded-lg font-bold flex items-center gap-2"><SafeIcon icon={FiPrinter} /> Print</button>
                  <button onClick={handleDownloadPDF} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold flex items-center gap-2 hover:bg-blue-700"><SafeIcon icon={FiDownload} /> Last ned PDF</button>
                </>
              )}
              {currentStep < 10 ? (
                <button onClick={()=> setCurrentStep(prev=> Math.min(10,prev + 1))} className="px-6 py-2.5 bg-[#4F46E5] text-white rounded-lg font-medium flex items-center gap-2">Neste <SafeIcon icon={FiArrowRight} /></button>
              ) : (
                <button onClick={handleSave} disabled={isSaving || !formData.finalization.archiveReady} className="px-8 py-2.5 bg-green-600 text-white rounded-lg font-bold flex items-center gap-2 shadow-lg disabled:opacity-50">
                  {isSaving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />} Fullfør & Arkiver
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestigationForm;